import React, { useEffect, useState } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  ShieldCheck,
  X,
  Eye,
  HeartPulse,
  Car,
  Umbrella,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Lock, Crown, ShieldAlert, Lightbulb, Rows3 } from "lucide-react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { useAuth } from "../context/AuthContext";
import { api, fmtINR, formatApiError } from "../lib/api";
import MoneyInput from "../components/ui/MoneyInput";
import { toast } from "sonner";
import UiDateField from "../components/ui/DateField";
import "../styles/date.css";

const POLICY_TYPES = [
  { value: "term_life", label: "Term Life", cash: false, group: "life" },
  { value: "endowment", label: "Endowment", cash: true, group: "life" },
  { value: "ulip", label: "ULIP", cash: true, group: "life" },
  { value: "money_back", label: "Money Back", cash: true, group: "life" },
  { value: "whole_life", label: "Whole Life", cash: true, group: "life" },
  { value: "health", label: "Health", cash: false, group: "health" },
  {
    value: "critical_illness",
    label: "Critical Illness",
    cash: false,
    group: "health",
  },
  {
    value: "personal_accident",
    label: "Personal Accident",
    cash: false,
    group: "health",
  },
  { value: "motor", label: "Motor / Vehicle", cash: false, group: "general" },
  { value: "other", label: "Other", cash: false, group: "general" },
];

const TYPE_LABEL = Object.fromEntries(
  POLICY_TYPES.map((t) => [t.value, t.label]),
);
const TYPE_DEF = Object.fromEntries(POLICY_TYPES.map((t) => [t.value, t]));

const FREQUENCIES = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "half_yearly", label: "Half-Yearly" },
  { value: "yearly", label: "Yearly" },
  { value: "single", label: "Single (one-time)" },
];

const groupIcon = (group) =>
  group === "health" ? HeartPulse : group === "general" ? Car : Umbrella;

export default function Insurance() {
  const { user, viewOnly } = useAuth();
  const isPremium = (user?.plan || "free").toLowerCase() !== "free";
  const [policies, setPolicies] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/insurance");
      setPolicies(data.policies || []);
      setTotals(data.totals || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this policy?")) return;
    try {
      await api.delete(`/insurance/${id}`);
      toast.success("Policy deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">
            Protection
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
            Insurance
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Premiums are tracked as a recurring expense; investment policies'
            cash value flows to your assets — automatically.
          </p>
        </div>
        {!viewOnly && (
          <div className="flex items-center gap-2">
            <Link
              data-testid="open-bulk-insurance"
              to="/dashboard/bulk-add/insurance"
              className="btn-secondary"
            >
              <Rows3 className="h-4 w-4" /> Bulk add
            </Link>
            <button
              onClick={() => {
                setEditing(null);
                setShowModal(true);
              }}
              className="btn-primary"
            >
              <Plus className="h-4 w-4" /> Add policy
            </button>
          </div>
        )}
      </div>

      {/* Totals */}
      {totals && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatBox
            label="Total Cover"
            value={fmtINR(totals.total_cover, { compact: true })}
            accent="text-sky-600"
          />
          <StatBox
            label="Annual Premium"
            value={fmtINR(totals.total_annual_premium, { compact: true })}
            accent="text-rose-600"
          />
          <StatBox
            label="Cash Value (asset)"
            value={fmtINR(totals.total_cash_value, { compact: true })}
            accent="text-emerald-600"
          />
          <StatBox label="Policies" value={totals.count} />
        </div>
      )}

      <AnalysisCard isPremium={isPremium} />

      <div className="card-soft overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-slate-500">
            Loading…
          </div>
        ) : policies.length === 0 ? (
          <div className="p-12 text-center">
            <ShieldCheck className="h-10 w-10 text-slate-300 mx-auto" />
            <h3 className="mt-3 font-display font-bold">No policies yet</h3>
            <p className="mt-1 text-sm text-slate-500">
              {viewOnly
                ? "This client has no insurance policies yet."
                : "Add your life, health and general insurance to see the full picture."}
            </p>
            {!viewOnly && (
              <button
                onClick={() => {
                  setEditing(null);
                  setShowModal(true);
                }}
                className="btn-primary mt-4 !text-xs"
              >
                <Plus className="h-3 w-3" /> Add policy
              </button>
            )}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100 dark:border-slate-800">
                <th className="text-left px-6 py-4 font-semibold">Policy</th>
                <th className="text-left px-6 py-4 font-semibold hidden md:table-cell">
                  Type
                </th>
                <th className="text-right px-6 py-4 font-semibold">Cover</th>
                <th className="text-right px-6 py-4 font-semibold hidden md:table-cell">
                  Annual Premium
                </th>
                <th className="text-right px-6 py-4 font-semibold hidden lg:table-cell">
                  Cash Value
                </th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {policies.map((p) => {
                const Icon = groupIcon(TYPE_DEF[p.policy_type]?.group);
                return (
                  <tr
                    key={p.id}
                    className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/40"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 grid h-9 w-9 place-items-center rounded-xl bg-sky-50 dark:bg-sky-500/10 text-sky-600">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900 dark:text-white">
                            {p.name}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {p.insurer || "—"}
                            {p.has_asset_value && (
                              <span className="ml-2 inline-flex items-center gap-1 text-emerald-600">
                                <ShieldCheck className="h-3 w-3" /> asset-linked
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">
                      <span className="text-xs font-medium rounded-md bg-slate-100 dark:bg-slate-800 px-2 py-1 text-slate-700 dark:text-slate-300">
                        {TYPE_LABEL[p.policy_type] || p.policy_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right num-mono font-semibold text-slate-900 dark:text-white">
                      {p.sum_assured ? fmtINR(p.sum_assured) : "—"}
                    </td>
                    <td className="px-6 py-4 text-right num-mono hidden md:table-cell text-rose-600">
                      {fmtINR(p.annual_premium)}
                    </td>
                    <td className="px-6 py-4 text-right num-mono hidden lg:table-cell text-emerald-600">
                      {p.has_asset_value ? fmtINR(p.current_value || 0) : "—"}
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      <button
                        onClick={() => setViewing(p)}
                        className="p-1.5 rounded-lg hover:bg-sky-50 dark:hover:bg-sky-500/10 text-slate-500 hover:text-sky-600"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                      {!viewOnly && (
                        <>
                          <button
                            onClick={() => {
                              setEditing(p);
                              setShowModal(true);
                            }}
                            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-emerald-600"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => onDelete(p.id)}
                            className="p-1.5 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-500/10 text-slate-500 hover:text-rose-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <PolicyModal
          policy={editing}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            load();
          }}
        />
      )}
      {viewing && (
        <PolicyViewModal policy={viewing} onClose={() => setViewing(null)} />
      )}
    </DashboardLayout>
  );
}

function PolicyModal({ policy, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: policy?.name || "",
    policy_type: policy?.policy_type || "term_life",
    insurer: policy?.insurer || "",
    policy_number: policy?.policy_number || "",
    sum_assured: policy?.sum_assured || "",
    premium_amount: policy?.premium_amount || "",
    premium_frequency: policy?.premium_frequency || "yearly",
    start_date: policy?.start_date
      ? String(policy.start_date).slice(0, 10)
      : "",
    maturity_date: policy?.maturity_date
      ? String(policy.maturity_date).slice(0, 10)
      : "",
    premium_paying_years: policy?.premium_paying_years || "",
    has_cash_value:
      policy?.has_cash_value ?? (policy ? false : TYPE_DEF["term_life"].cash),
    current_value: policy?.current_value || "",
    maturity_value: policy?.maturity_value || "",
    expected_return: policy?.expected_return || "",
    nominee: policy?.nominee || "",
    notes: policy?.notes || "",
    is_active: policy?.is_active ?? true,
  });
  const [pickers, setPickers] = useState({});
  const [saving, setSaving] = useState(false);

  const typeDef = TYPE_DEF[form.policy_type] || TYPE_DEF.term_life;
  // Cash value only makes sense for life-investment products.
  const cashEligible =
    typeDef.group === "life" && typeDef.value !== "term_life";
  const showCash = cashEligible && form.has_cash_value;
  const isSingle = form.premium_frequency === "single";

  const onTypeChange = (t) => {
    const def = TYPE_DEF[t];
    setForm((f) => ({
      ...f,
      policy_type: t,
      has_cash_value:
        def.value !== "term_life" && def.group === "life" ? def.cash : false,
    }));
  };

  const num = (v) =>
    v !== "" && v != null ? Number(String(v).replace(/,/g, "")) : undefined;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        policy_type: form.policy_type,
        insurer: form.insurer || undefined,
        policy_number: form.policy_number || undefined,
        sum_assured: num(form.sum_assured),
        premium_amount: num(form.premium_amount) || 0,
        premium_frequency: form.premium_frequency,
        start_date: form.start_date || undefined,
        maturity_date: form.maturity_date || undefined,
        premium_paying_years: form.premium_paying_years
          ? Number(form.premium_paying_years)
          : undefined,
        has_cash_value: !!showCash,
        nominee: form.nominee || undefined,
        notes: form.notes || undefined,
        is_active: !!form.is_active,
      };
      if (showCash) {
        payload.current_value = num(form.current_value);
        payload.maturity_value = num(form.maturity_value);
        payload.expected_return = form.expected_return
          ? Number(form.expected_return)
          : undefined;
      }
      if (policy?.id) await api.put(`/insurance/${policy.id}`, payload);
      else await api.post("/insurance", payload);
      toast.success(policy ? "Policy updated" : "Policy added");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="w-full max-w-2xl card-soft p-6 my-8 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white">
              {policy ? "Edit policy" : "Add insurance policy"}
            </h2>
            <button
              onClick={onClose}
              className="grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <form onSubmit={onSubmit} className="space-y-3">
            <Field label="Policy name" required>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-soft"
                placeholder="e.g. HDFC Click 2 Protect"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type" required>
                <select
                  value={form.policy_type}
                  onChange={(e) => onTypeChange(e.target.value)}
                  className="input-soft"
                >
                  {POLICY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Insurer">
                <input
                  value={form.insurer}
                  onChange={(e) =>
                    setForm({ ...form, insurer: e.target.value })
                  }
                  className="input-soft"
                  placeholder="e.g. HDFC Life"
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Sum Assured / Cover (₹)">
                <MoneyInput
                  value={form.sum_assured}
                  onValueChange={(raw) =>
                    setForm({ ...form, sum_assured: raw })
                  }
                  placeholder="0"
                />
              </Field>
              <Field label="Policy Number">
                <input
                  value={form.policy_number}
                  onChange={(e) =>
                    setForm({ ...form, policy_number: e.target.value })
                  }
                  className="input-soft"
                />
              </Field>
            </div>

            {/* Premium block */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Premium (recurring expense)
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Premium Amount (₹)" required>
                  <MoneyInput
                    required
                    value={form.premium_amount}
                    onValueChange={(raw) =>
                      setForm({ ...form, premium_amount: raw })
                    }
                    placeholder="0"
                  />
                </Field>
                <Field label="Frequency">
                  <select
                    value={form.premium_frequency}
                    onChange={(e) =>
                      setForm({ ...form, premium_frequency: e.target.value })
                    }
                    className="input-soft"
                  >
                    {FREQUENCIES.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              {!isSingle && (
                <Field label="Premium Paying Term (years)">
                  <input
                    type="number"
                    min="0"
                    value={form.premium_paying_years}
                    onChange={(e) =>
                      setForm({ ...form, premium_paying_years: e.target.value })
                    }
                    className="input-soft"
                    placeholder="e.g. 20"
                  />
                </Field>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DateField
                label="Start Date"
                value={form.start_date}
                open={pickers.start}
                onToggle={() => setPickers({ start: !pickers.start })}
                onSelect={(d) => {
                  setForm({ ...form, start_date: d });
                  setPickers({});
                }}
              />
              <DateField
                label="Maturity Date"
                value={form.maturity_date}
                open={pickers.maturity}
                onToggle={() => setPickers({ maturity: !pickers.maturity })}
                onSelect={(d) => {
                  setForm({ ...form, maturity_date: d });
                  setPickers({});
                }}
              />
            </div>

            {/* Cash value (only for investment-type life policies) */}
            {cashEligible && (
              <div className="rounded-xl border border-emerald-200 dark:border-emerald-500/20 bg-emerald-50/40 dark:bg-emerald-500/5 p-3 space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                  <input
                    type="checkbox"
                    checked={!!form.has_cash_value}
                    onChange={(e) =>
                      setForm({ ...form, has_cash_value: e.target.checked })
                    }
                    className="rounded"
                  />
                  This policy has a fund / surrender value (counts as an asset)
                </label>
                {showCash && (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Current Fund / Surrender Value (₹)">
                      <MoneyInput
                        value={form.current_value}
                        onValueChange={(raw) =>
                          setForm({ ...form, current_value: raw })
                        }
                        placeholder="0"
                      />
                    </Field>
                    <Field label="Expected Maturity Value (₹)">
                      <MoneyInput
                        value={form.maturity_value}
                        onValueChange={(raw) =>
                          setForm({ ...form, maturity_value: raw })
                        }
                        placeholder="0"
                      />
                    </Field>
                    <Field label="Expected Return % p.a.">
                      <input
                        type="number"
                        step="0.1"
                        value={form.expected_return}
                        onChange={(e) =>
                          setForm({ ...form, expected_return: e.target.value })
                        }
                        className="input-soft"
                        placeholder="6"
                      />
                    </Field>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Nominee">
                <input
                  value={form.nominee}
                  onChange={(e) =>
                    setForm({ ...form, nominee: e.target.value })
                  }
                  className="input-soft"
                />
              </Field>
              <Field label="Status">
                <select
                  value={form.is_active ? "active" : "inactive"}
                  onChange={(e) =>
                    setForm({ ...form, is_active: e.target.value === "active" })
                  }
                  className="input-soft"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Lapsed / Closed</option>
                </select>
              </Field>
            </div>

            <Field label="Notes">
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="input-soft min-h-[60px]"
                rows={2}
              />
            </Field>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary flex-1"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function PolicyViewModal({ policy, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="w-full max-w-xl card-soft p-6 my-8 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
                {policy.name}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {TYPE_LABEL[policy.policy_type] || policy.policy_type}
                {policy.insurer ? ` · ${policy.insurer}` : ""}
              </p>
            </div>
            <button
              onClick={onClose}
              className="grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ViewField
              label="Sum Assured"
              value={policy.sum_assured ? fmtINR(policy.sum_assured) : "—"}
            />
            <ViewField
              label="Annual Premium (expense)"
              value={fmtINR(policy.annual_premium || 0)}
            />
            <ViewField
              label="Monthly Premium"
              value={fmtINR(policy.monthly_premium || 0)}
            />
            <ViewField
              label="Cash Value (asset)"
              value={
                policy.has_asset_value ? fmtINR(policy.current_value || 0) : "—"
              }
            />
            <ViewField
              label="Maturity Value"
              value={
                policy.maturity_value ? fmtINR(policy.maturity_value) : "—"
              }
            />
            <ViewField label="Start Date" value={policy.start_date || "—"} />
            <ViewField
              label="Maturity Date"
              value={policy.maturity_date || "—"}
            />
            <ViewField label="Nominee" value={policy.nominee || "—"} />
            <ViewField
              label="Policy Number"
              value={policy.policy_number || "—"}
            />
            <ViewField
              label="Status"
              value={policy.is_active ? "Active" : "Lapsed / Closed"}
            />
          </div>
          <div className="mt-5">
            <ViewField label="Notes" value={policy.notes || "—"} full />
          </div>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, accent = "text-slate-900 dark:text-white" }) {
  return (
    <div className="card-soft p-5">
      <div className="text-xs text-slate-500 uppercase tracking-wide">
        {label}
      </div>
      <div className={`mt-1 num-mono text-xl font-bold ${accent}`}>{value}</div>
    </div>
  );
}

function AnalysisCard({ isPremium }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isPremium) {
      setLoading(false);
      return;
    }
    api
      .get("/insurance/analysis")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isPremium]);

  // Free tier → locked teaser
  if (!isPremium) {
    return (
      <div className="card-soft p-6 mb-6">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-50 dark:bg-amber-500/10 text-amber-600">
            <Lock className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-display text-lg font-bold text-slate-900 dark:text-white">
                Insurance Analysis
              </h3>
              <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-blue-600 text-white">
                Premium
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500 max-w-xl">
              Understand the cover you already hold — how cost-efficient it is,
              how much is pure protection vs. investment-linked, and how it
              compares to your loans.
            </p>
            <Link
              to="/dashboard/settings"
              className="btn-primary !text-xs mt-4 inline-flex"
            >
              <Crown className="h-4 w-4" /> Unlock with Premium
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div className="card-soft p-6 mb-6 h-28 animate-pulse" />;
  if (!data) return null;

  const t = data.totals || {};
  if (!t.policy_count) {
    return (
      <div className="card-soft p-6 mb-6 text-sm text-slate-500">
        Add a policy to see your insurance analysis.
      </div>
    );
  }

  return (
    <div className="card-soft p-6 mb-6" data-testid="insurance-analysis">
      <div className="flex items-start gap-2 mb-4">
        <ShieldAlert className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
        <div>
          <h3 className="font-display text-lg font-bold text-slate-900 dark:text-white">
            Insurance Analysis
          </h3>
          <p className="text-xs text-slate-500">{data.disclaimer}</p>
        </div>
      </div>

      {/* Key facts about the cover you hold */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Fact
          label="Total cover"
          value={fmtINR(t.total_cover, { compact: true })}
        />
        <Fact
          label="Annual premium"
          value={fmtINR(t.annual_premium, { compact: true })}
          accent="text-rose-600"
        />
        <Fact
          label="Cover per ₹1 premium"
          value={
            t.cover_per_premium != null
              ? `₹${Math.round(t.cover_per_premium)}`
              : "—"
          }
          accent="text-emerald-600"
        />
        <Fact
          label="Pure protection"
          value={
            t.pure_protection_pct != null
              ? `${Math.round(t.pure_protection_pct)}%`
              : "—"
          }
          sub="of life cover"
        />
      </div>

      {/* By type breakdown */}
      {data.by_type?.length > 0 && (
        <div className="mt-5 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-slate-400 bg-slate-50 dark:bg-slate-800/40">
            <div className="col-span-5">Policy type</div>
            <div className="col-span-2 text-right">Count</div>
            <div className="col-span-3 text-right">Cover</div>
            <div className="col-span-2 text-right">Premium/yr</div>
          </div>
          {data.by_type.map((b) => (
            <div
              key={b.type}
              className="grid grid-cols-12 px-4 py-2.5 text-sm border-t border-slate-100 dark:border-slate-800"
            >
              <div className="col-span-5 font-medium text-slate-800 dark:text-slate-100 truncate">
                {TYPE_LABEL[b.type] || b.type}
              </div>
              <div className="col-span-2 text-right text-slate-500">
                {b.count}
              </div>
              <div className="col-span-3 text-right num-mono">
                {fmtINR(b.cover, { compact: true })}
              </div>
              <div className="col-span-2 text-right num-mono text-slate-500">
                {fmtINR(b.annual_premium, { compact: true })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Neutral observations */}
      {data.observations?.length > 0 && (
        <div className="mt-5 space-y-2">
          {data.observations.map((o, i) => (
            <div
              key={i}
              className="flex items-start gap-3 rounded-xl border border-slate-200 dark:border-slate-700 p-3"
            >
              <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-sky-50 dark:bg-sky-500/10 text-sky-600">
                <Lightbulb className="h-4 w-4" />
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                {o.text}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Fact({
  label,
  value,
  sub,
  accent = "text-slate-900 dark:text-white",
}) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className={`num-mono text-lg font-bold mt-1 ${accent}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

// Thin wrapper over the shared, cross-browser DateField.
function DateField({ label, value, onSelect }) {
  return (
    <Field label={label}>
      <UiDateField value={value || ""} onChange={(v) => onSelect(v)} />
    </Field>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </label>
      {children}
    </div>
  );
}

function ViewField({ label, value, full = false }) {
  return (
    <div className={full ? "" : ""}>
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
        {label}
      </div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3 text-sm text-slate-900 dark:text-white bg-slate-50 dark:bg-slate-900/40">
        {value}
      </div>
    </div>
  );
}
